import { useEffect, useState } from "react";
import * as React from "react";

import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import { Box, Button, Modal, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { ethers } from "ethers";

import {
  _CollectionType,
  TokenDistributionType,
} from "apollo/graphql.generated";
import { PayoutAddressInput } from "components/sale/PayoutAddressInput";
import useManageSale from "~/features/manage-collection/mint-vector/useManageSale";
import { GetManageCollectionQuery } from "~/features/manage-collection/v2.queries.graphql.generated";
import { useUpdatePaymentRecipientOnPrivateSalesMutation } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

type Props = {
  collectionId: string;
  paymentRecipient?: string;
  publicMintVector?: GetManageCollectionQuery["getManageCollection"]["mintVectors"][0];
  isOpened: boolean;
  onClose: () => void;
  distributionType: TokenDistributionType;
  collectionType: _CollectionType;
  isCollectionReveal: boolean | null;
  refetchCollection?: () => void;
};

interface EditPaymentRecipientFormValues {
  paymentRecipientAddress: string;
  paymentRecipientEOA: string;
}

export const EditPaymentRecipient = ({
  isOpened,
  onClose,
  paymentRecipient,
  publicMintVector,
  distributionType,
  collectionId,
  collectionType,
  refetchCollection,
  isCollectionReveal,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    buttonDisabled,
    buttonLabel,
    error,
    loading: loadingUpdate,
    updatePaymentRecipientForPublicSale,
  } = useManageSale(
    {
      collectionType,
      id: collectionId,
      reveal: isCollectionReveal,
    },
    onClose,
    publicMintVector
  );

  const [updatePaymentRecipientOnPrivateSales, { error: errorPrivateSale }] =
    useUpdatePaymentRecipientOnPrivateSalesMutation();

  const closeModal = () => {
    form.reset();
    onClose();
  };
  const form = useForm<EditPaymentRecipientFormValues>({
    initialValues: {
      paymentRecipientAddress: paymentRecipient || "",
      paymentRecipientEOA: paymentRecipient || "",
    },
    validate: (values) => ({
      paymentRecipientEOA:
        !values.paymentRecipientAddress ||
        !ethers.utils.isAddress(values.paymentRecipientAddress)
          ? "Valid Payment recipient is required"
          : null,
    }),
  });

  const onSubmit = form.onSubmit(async (values) => {
    setLoading(true);
    if (publicMintVector?.id) {
      await updatePaymentRecipientForPublicSale(values.paymentRecipientAddress);
      setLoading(false);
    } else {
      try {
        await updatePaymentRecipientOnPrivateSales({
          variables: {
            paymentRecipient: values.paymentRecipientAddress,
            collectionId,
          },
        });
        refetchCollection?.();
        closeModal();
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    form.validate();
  }, [form.values]);

  return (
    <Modal size="lg" opened={isOpened} onClose={onClose} title="Edit payout">
      <Box mx="auto">
        <form onSubmit={onSubmit}>
          <PayoutAddressInput
            onChangeLoading={(loading) => setLoading(loading)}
            onChange={(customRecipient) => {
              form.setFieldValue(
                "paymentRecipientAddress",
                customRecipient.address || ""
              );
            }}
            onError={(error) =>
              form.setFieldError("paymentRecipientEOA", error)
            }
            distributionType={distributionType}
            addressInputProps={form.getInputProps("paymentRecipientEOA")}
          />
          <ModalSection>
            <Button
              type="submit"
              size="xl"
              fullWidth
              loading={loadingUpdate || loading || buttonDisabled}
            >
              {buttonLabel || "Save changes"}
            </Button>
            {(error || errorPrivateSale) && (
              <Text size="xs" mt={5} color={STATUS_COLOR.ERROR}>
                {error || "Error updating payout"}
              </Text>
            )}
            {publicMintVector && (
              <Text size="xs" align="center" mt={8}>
                This action requires a transaction
              </Text>
            )}
          </ModalSection>
        </form>
      </Box>
    </Modal>
  );
};
